import { useTranslation } from 'react-i18next';
import { formatQuantityForUnit } from '~/utils';
import type { OrderForBuilder } from '~/api/order';
import { computeSourcingCoverage } from '~/pages/order-builder/sourcingCoverage';
import type { Cart } from '~/graphql/graphql';

type SourcingCoverageGuideProps = {
  order: OrderForBuilder;
  carts: Cart[];
};

export function SourcingCoverageGuide({
  order,
  carts,
}: Readonly<SourcingCoverageGuideProps>) {
  const { t } = useTranslation('order-builder');

  const coverageRows = computeSourcingCoverage(order, carts);

  return (
    <div className="bg-neut-light rounded-lg border border-neut-border shadow-sm">
      <div className="px-6 py-4 border-b border-neut-border bg-neut-50">
        <h2 className="text-sm font-semibold text-neut-900">
          {t('order_builder.sourcing.coverage_title')}
        </h2>
      </div>
      <div className="px-6 py-4 space-y-3">
        {coverageRows.map((row) => {
          const isFullyCovered = row.isCovered;
          const progressPercent =
            row.required > 0
              ? Math.min(100, (row.covered / row.required) * 100)
              : 0;
          return (
            <div key={row.platformProductsLengthId}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-neut-900 flex items-center gap-2">
                  {row.productName}
                  <span className="px-2 py-1 bg-neut-100 rounded-md text-xs">
                    {row.lengthDisplayValue}
                  </span>
                </span>
                <span
                  className={
                    isFullyCovered
                      ? 'text-success-600 font-semibold'
                      : 'text-warning-600 font-semibold'
                  }
                  data-testid="sourcing-coverage-value"
                >
                  {formatQuantityForUnit({
                    value: row.covered,
                    unit: row.unit,
                  })}{' '}
                  /{' '}
                  {formatQuantityForUnit({
                    value: row.required,
                    unit: row.unit,
                  })}
                  {isFullyCovered ? ' ✓' : ''}
                </span>
              </div>
              <div className="h-1.5 bg-neut-100 rounded-full">
                <div
                  className={`h-1.5 rounded-full ${
                    isFullyCovered ? 'bg-success-600' : 'bg-warning-500'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          );
        })}
        <p className="text-xs text-neut-400 pt-1">
          {t('order_builder.sourcing.coverage_note')}
        </p>
      </div>
    </div>
  );
}
